import React, { useState, useEffect, useCallback, useMemo } from "react";
import { MDBContainer, MDBSpinner, MDBTooltip } from "mdb-react-ui-kit";
import { ActionButton, DataTable } from "../ui";
import { api, getTenantApiScope } from "../../services";
import { toast } from "react-toastify";
import {
  normalizeRestaurantId,
  restaurantIdFromAuthToken,
  roleIdsAssignedToCurrentUser,
} from "../../utils/utils";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../Modals/ConfirmModal";
import RoleWizardModal from "./RoleWizardModal";
import Toolbar from "../Toolbar";
import { usePermission } from "../../hooks/usePermission";
import { RBAC_MODULES, PERMISSION_ACTIONS } from "../../config/routePermissions";
import { translateRbacApiMessage } from "../../utils/translateRbacApiMessage";

const BRAND = "#083B4C";
const FONT_UI = "'Inter', 'Noto Sans Arabic', 'Segoe UI', sans-serif";
const SEARCH_DEBOUNCE_MS = 350;

/**
 * Roles CRUD for the current tenant; uses paginated GET /v1/roles when page/limit are sent.
 */
function RolesPage() {
  const { t, i18n } = useTranslation("translation");
  const tp = (key, opts) => t(`userManagementPage.${key}`, opts);
  const { can, bypass, isStaff, summary } = usePermission();

  const canCreate = can(RBAC_MODULES.ROLES, PERMISSION_ACTIONS.ADD);
  const canView = can(RBAC_MODULES.ROLES, PERMISSION_ACTIONS.VIEW);
  const canUpdate = can(RBAC_MODULES.ROLES, PERMISSION_ACTIONS.EDIT);
  const canDelete = can(RBAC_MODULES.ROLES, PERMISSION_ACTIONS.DELETE);
  const canOpenRole = canView || canUpdate;
  const noPermissionTip = t("noPermissionToAccessThis");
  const ownRoleEditTip = t("rbac.cannotEditRoleAssignedToYou");
  const defaultRoleDeleteTip = t("rbac.cannotDeleteDefaultRole");
  const myAssignedRoleIds = useMemo(
    () => roleIdsAssignedToCurrentUser(summary),
    [summary]
  );

  const [restaurantId, setRestaurantId] = useState(null);
  const [tenantResolved, setTenantResolved] = useState(false);

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [editInitialUserCount, setEditInitialUserCount] = useState(0);

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState({
    title: "",
    message: "",
    action: () => {},
    actionData: null,
  });

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    // Company/admin users don't need tenant resolution, they can work without restaurantId
    if (bypass) {
      setTenantResolved(true);
      return;
    }

    // Staff users need tenant resolution
    const fromJwt = restaurantIdFromAuthToken();
    if (fromJwt) {
      setRestaurantId(fromJwt);
      setTenantResolved(true);
      return;
    }

    const raw = localStorage.getItem("user");
    if (raw) {
      try {
        const u = JSON.parse(raw);
        const fromUser = normalizeRestaurantId(u.restaurantId);
        if (fromUser) {
          setRestaurantId(fromUser);
          setTenantResolved(true);
          return;
        }
      } catch {
        /* continue */
      }
    }

    const fromDashboardScope = normalizeRestaurantId(getTenantApiScope().storeId);
    if (fromDashboardScope) {
      setRestaurantId(fromDashboardScope);
      setTenantResolved(true);
      return;
    }

    api
      .get("/stores/my")
      .then((res) => {
        const firstStore = res.data?.stores?.[0];
        const id = normalizeRestaurantId(firstStore?._id);
        if (id) setRestaurantId(id);
      })
      .catch(() => {})
      .finally(() => setTenantResolved(true));
  }, [bypass]);

  const fetchRoles = useCallback(async () => {
    // Staff users need tenant resolution, company/admin users don't
    if (isStaff && !tenantResolved) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();

      // Do not send restaurantId/storeId for staff: backend picks the assigned store when single,
      // or merges all employer stores + account-level roles when multi-store. Sending dashboard
      // session store id here often mismatches User.restaurantId → 403 "Not allowed for this restaurant".

      params.set("page", String(currentPage));
      params.set("limit", String(itemsPerPage));
      const q = debouncedSearch.trim();
      if (q) params.set("search", q);
      params.set("sortBy", sortField);
      params.set("sortOrder", sortOrder);

      const res = await api.get(`/v1/roles?${params}`);
      const payload = res.data;
      setRoles(Array.isArray(payload?.data) ? payload.data : []);
      setTotalPages(payload?.pagination?.pages || 1);
      setTotalCount(payload?.pagination?.total ?? 0);
    } catch (err) {
      toast.error(
        translateRbacApiMessage(err.response?.data?.message, t) || t("Failed to load roles")
      );
      setRoles([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [isStaff, tenantResolved, currentPage, itemsPerPage, debouncedSearch, sortField, sortOrder, t]);

  const applySort = (field, order) => {
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (tenantResolved) {
      fetchRoles();
    }
  }, [tenantResolved, fetchRoles]);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  useEffect(() => {
    setCurrentPage((p) => {
      if (totalPages < 1) return 1;
      return p > totalPages ? totalPages : p;
    });
  }, [totalPages]);

  function openCreateModal() {
    if (!canCreate) {
      toast.error(noPermissionTip);
      return;
    }
    setEditingRoleId(null);
    setEditInitialUserCount(0);
    setShowRoleModal(true);
  }

  function openEditModal(roleRow) {
    if (!canOpenRole) {
      toast.error(noPermissionTip);
      return;
    }
    if (myAssignedRoleIds.has(String(roleRow._id))) {
      return;
    }
    // Company/admin users can edit any role, staff users need restaurant context
    const rid =
      normalizeRestaurantId(restaurantId) ??
      normalizeRestaurantId(getTenantApiScope().storeId);
    
    if (!rid && isStaff) {
      toast.error(tp("sessionNoRestaurant"));
      return;
    }
    
    setEditingRoleId(roleRow._id);
    setEditInitialUserCount(roleRow.userCount ?? 0);
    setShowRoleModal(true);
  }

  function confirmDelete(role) {
    if (!canDelete) {
      toast.error(noPermissionTip);
      return;
    }
    if (role.isDefaultRole === true) {
      toast.error(defaultRoleDeleteTip);
      return;
    }
    if ((role.userCount ?? 0) > 0) {
      return;
    }
    setConfirmData({
      title: t("Delete Role"),
      message: t("deleteRoleConfirmMessage", { name: role.name }),
      action: async () => {
        try {
          await api.delete(`/v1/roles/${role._id}`);
          toast.success(t("rbac.roleDeleted"));
          fetchRoles();
        } catch (err) {
          const raw = err.response?.data?.message;
          const msg = raw ? translateRbacApiMessage(raw, t) : t("rbac.deleteFailed");
          const count = err.response?.data?.assignmentCount;
          toast.error(count != null ? `${msg} (${count})` : msg);
        }
      },
      actionData: null,
    });
    setShowConfirm(true);
  }

  const tenantId = bypass 
    ? (normalizeRestaurantId(restaurantId) ?? normalizeRestaurantId(getTenantApiScope().storeId))
    : (normalizeRestaurantId(restaurantId) ?? normalizeRestaurantId(getTenantApiScope().storeId));

  const formatCreatedCell = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "—";
      const lng = i18n.language || "en-US";
      const line = d.toLocaleString(lng, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
      return <span className="rm-created-at-line">{line}</span>;
    } catch {
      return "—";
    }
  };

  const roleColumns = useMemo(
    () => [
      {
        id: "name",
        header: t("Role name"),
        sortable: true,
        strong: true,
        minWidth: 160,
        render: (r) => (
          <div
            className="rm-role-name d-flex align-items-center gap-2 flex-wrap"
            style={{ color: BRAND, fontWeight: 600 }}
          >
            <span>{r.name}</span>
            {r.isDefaultRole === true ? (
              <span className="rm-default-role-badge">{t("rbac.defaultRoleBadge")}</span>
            ) : null}
          </div>
        ),
      },
      {
        id: "description",
        header: t("Description"),
        sortable: true,
        minWidth: 200,
        render: (r) => r.description || "—",
      },
      {
        id: "userCount",
        header: t("Users"),
        sortable: true,
        minWidth: 88,
        render: (r) => r.userCount ?? 0,
      },
      {
        id: "createdAt",
        header: t("Created At"),
        sortable: true,
        minWidth: 190,
        className: "rm-td-created",
        render: (r) => formatCreatedCell(r.createdAt),
      },
      {
        id: "actions",
        header: t("Actions"),
        minWidth: 220,
        headerClassName: "oh-table-th--actions",
        className: "oh-table-td--actions",
        render: (r) => {
          const roleUserCount = r.userCount ?? 0;
          const isDefaultRole = r.isDefaultRole === true;
          const deleteBlockedByUsers = roleUserCount > 0;
          const deleteBlockedByDefault = isDefaultRole;
          const editBlockedBySelfAssignment = myAssignedRoleIds.has(String(r._id));
          const editLabel = canUpdate ? tp("editUppercase") : t("rbac.viewRole");
          return (
            <div className="rm-actions-group oh-table-actions">
              {canOpenRole && !editBlockedBySelfAssignment ? (
                <ActionButton
                  variant="edit"
                  onClick={() => openEditModal(r)}
                  aria-label={`${editLabel} role ${r.name}`}
                >
                  {editLabel}
                </ActionButton>
              ) : canOpenRole && editBlockedBySelfAssignment ? (
                <MDBTooltip tag="span" title={ownRoleEditTip}>
                  <span className="d-inline-block">
                    <ActionButton
                      variant="edit"
                      disabled
                      aria-label={`${editLabel} role ${r.name}`}
                    >
                      {editLabel}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              ) : (
                <MDBTooltip tag="span" title={noPermissionTip}>
                  <span className="d-inline-block">
                    <ActionButton
                      variant="edit"
                      disabled
                      aria-label={`${editLabel} role ${r.name}`}
                    >
                      {editLabel}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              )}
              {canDelete && !deleteBlockedByUsers && !deleteBlockedByDefault ? (
                <ActionButton
                  variant="delete"
                  onClick={() => confirmDelete(r)}
                  aria-label={`${tp("deleteUppercase")} role ${r.name}`}
                >
                  {tp("deleteUppercase")}
                </ActionButton>
              ) : canDelete && deleteBlockedByDefault ? (
                <MDBTooltip tag="span" title={defaultRoleDeleteTip}>
                  <span className="d-inline-block">
                    <ActionButton
                      variant="delete"
                      disabled
                      aria-label={`${tp("deleteUppercase")} role ${r.name}`}
                    >
                      {tp("deleteUppercase")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              ) : canDelete && deleteBlockedByUsers ? (
                <MDBTooltip tag="span" title={t("rbac.cannotDeleteRoleUsersConnected")}>
                  <span className="d-inline-block">
                    <ActionButton
                      variant="delete"
                      disabled
                      aria-label={`${tp("deleteUppercase")} role ${r.name}`}
                    >
                      {tp("deleteUppercase")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              ) : (
                <MDBTooltip tag="span" title={noPermissionTip}>
                  <span className="d-inline-block">
                    <ActionButton
                      variant="delete"
                      disabled
                      aria-label={`${tp("deleteUppercase")} role ${r.name}`}
                    >
                      {tp("deleteUppercase")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              )}
            </div>
          );
        },
      },
    ],
    [
      t,
      tp,
      canOpenRole,
      canUpdate,
      canDelete,
      noPermissionTip,
      ownRoleEditTip,
      defaultRoleDeleteTip,
      myAssignedRoleIds,
    ]
  );

  if (!tenantResolved) {
    return (
      <MDBContainer className="py-5 text-center">
        <MDBSpinner />
      </MDBContainer>
    );
  }

  if (!tenantId && isStaff) {
    return (
      <MDBContainer className="py-4">
        <div className="alert alert-warning">{t("Account access required to view roles.")}</div>
      </MDBContainer>
    );
  }

  return (
    <div
      dir={i18n.dir()}
      lang={i18n.language}
      className="roles-management-clean oh-page"
      style={{
        backgroundColor: "#F8F9FA",
        minHeight: "100vh",
        fontFamily: FONT_UI,
      }}
    >
      <style>{tableStyles}</style>

        <div className="oh-page-header rm-header">
          <h1 className="page-title-header">
            {t("Roles")}
          </h1>
        </div>

        <div className="oh-page-section">
          <Toolbar
            hideIcons={true}
            showSearch={true}
            searchQuery={searchQuery}
            handleSearch={(e) => setSearchQuery(e.target.value)}
            searchPlaceHolder={tp("searchPlaceholderPlain")}
          />
        </div>

        <div className="oh-page-section">
          <DataTable
            tableId="roles-management"
            title={t("Roles")}
            columns={roleColumns}
            data={roles}
            rowKey="_id"
            loading={loading}
            isRtl={i18n.dir() === "rtl"}
            sort={{
              column: sortField,
              direction: sortOrder,
              onChange: ({ column, direction }) => applySort(column, direction),
            }}
            primaryAction={
              canCreate
                ? { label: t("Add Role"), onClick: openCreateModal }
                : {
                    label: t("Add Role"),
                    onClick: openCreateModal,
                    disabled: true,
                    title: noPermissionTip,
                  }
            }
            emptyState={
              <div className="rm-empty-content">
                <h3>{t("No roles to display")}</h3>
                <p>{t("rbac.noRolesMatchingCriteria")}</p>
              </div>
            }
            pagination={{
              page: currentPage,
              pageSize: itemsPerPage,
              total: totalCount,
              onPageChange: setCurrentPage,
              onPageSizeChange: (n) => {
                setItemsPerPage(n);
                setCurrentPage(1);
              },
              pageSizeOptions: [10, 25, 50],
              showLabel: tp("show"),
              ofLabel: t("of", { defaultValue: "of" }),
              resultsLabel: t("results", { defaultValue: "results" }),
              isRtl: i18n.dir() === "rtl",
              previousLabel: t("previous"),
              nextLabel: t("next"),
            }}
          />
        </div>

      <RoleWizardModal
        show={showRoleModal}
        onHide={() => {
          setShowRoleModal(false);
          setEditingRoleId(null);
          setEditInitialUserCount(0);
        }}
        restaurantId={tenantId}
        roleId={editingRoleId}
        initialUserCount={editInitialUserCount}
        onSaved={fetchRoles}
        canCreate={canCreate}
        canUpdate={canUpdate}
        canDelete={canDelete}
      />

      <ConfirmModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        title={confirmData.title}
        message={confirmData.message}
        action={confirmData.action}
        actionData={confirmData.actionData}
        visibility={null}
      />
    </div>
  );
}

export default RolesPage;

const tableStyles = `
.roles-management-clean {
  font-family: ${FONT_UI};
  width: 100%;
}

.rm-loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  background: #FFFFFF;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
}

.rm-td-created {
  vertical-align: middle;
}

.rm-created-at-line {
  display: block;
  white-space: nowrap;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
  color: #030f1b;
  font-variant-numeric: tabular-nums;
}

.rm-empty-content h3 {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
}

.rm-empty-content p {
  font-size: 14px;
  color: #6B7280;
  margin: 0;
}

.rm-actions-group {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.rm-btn {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rm-btn-edit {
  color: #2563EB;
  border-color: #2563EB;
}

.rm-btn-edit:hover:not(:disabled) {
  background: #EBF4FF;
}

.rm-default-role-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 4px;
  color: #475569;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
`;
